import React, { useCallback, useMemo } from "react";
import { FraudPerpetratorInfoRow, PerpetratorStatus } from "../../../../../../src/api/fmr1";
import { useAppDispatch, useAppSelector } from "../../../../../../src/app/store";
import { OptionNameValuePair } from "../../../../../../src/lib/interfaces/types";
import { ColumnType } from "../../../../../components/ui/baseDataTable";
import {
  addPerpetratorInfoRow,
  deletePerpetratorInfoRow,
  selectFMR1DraftPerpetratorInfoRows,
  updatePerpetratorInfoRow,
} from "../../../logic/frm1Slice";
import {
  generateRowId,
  getInsertedSno,
  getInsertedSortOrder,
  getSnoUpdates,
  sortRowsBySortOrder,
} from "../common/utils";
import { FMRTable, FMRTableColumn } from "../common/FMRTable";

export type PerpetratorDetailsTableHandle = {
  getDirtyRows: () => FraudPerpetratorInfoRow[];
  getDirtyUpsertPayload: () => any[];
  getAllRows: () => FraudPerpetratorInfoRow[];
  markAllClean: () => void;
  resetRows: (nextRows: FraudPerpetratorInfoRow[]) => void;
};

export interface PerpetratorDetailsTableProps {
  onDirtyChange?: (isDirty: boolean) => void;
  onDirtyUpsertPayloadChange?: (payload: any[]) => void;
  initialRows?: FraudPerpetratorInfoRow[];
}

const buildEmptyRow = (): FraudPerpetratorInfoRow => ({
  row_id: generateRowId(),
  isDirty: true,
  isAdded: true,
});

const PERPETRATOR_STATUS_OPTIONS: OptionNameValuePair<PerpetratorStatus>[] = [
  { name: "Principal", value: PerpetratorStatus.PRINCIPAL },
];

export const PerpetratorDetailsTable: React.FC<PerpetratorDetailsTableProps> = () => {
  const dispatch = useAppDispatch();

  const rows = useAppSelector(selectFMR1DraftPerpetratorInfoRows);
  const sortedRows = useMemo(() => sortRowsBySortOrder(rows), [rows]);

  const handleAddRow = useCallback((rowIndex: number) => {
    const prevRow = sortedRows[rowIndex];
    const prevSort = prevRow?.sort_order;
    const nextSort = sortedRows[rowIndex + 1]?.sort_order;
    const newRow: FraudPerpetratorInfoRow = {
      ...buildEmptyRow(),
      sort_order: getInsertedSortOrder(prevSort, nextSort),
      sno: getInsertedSno(prevRow),
    };

    const updates = getSnoUpdates(sortedRows, rowIndex + 1, +1);
    updates.forEach((u) => {
      dispatch(updatePerpetratorInfoRow({ id: u.id, changes: { sno: u.newSno } }));
    });

    dispatch(addPerpetratorInfoRow({ row: newRow }));
  }, [dispatch, sortedRows]);

  const handleDeleteRow = useCallback(async (rowIndex: number) => {
    const deletedRow = sortedRows[rowIndex];
    if (!deletedRow) return;

    const updates = getSnoUpdates(sortedRows, rowIndex + 1, -1);
    updates.forEach((u) => {
      dispatch(updatePerpetratorInfoRow({ id: u.id, changes: { sno: u.newSno } }));
    });

    dispatch(deletePerpetratorInfoRow({ id: deletedRow.row_id }));

    if (deletedRow.isAdded) return;
  }, [dispatch, sortedRows]);

  const handleChange = useCallback(
    (rowIndex: number, value: any, field: keyof FraudPerpetratorInfoRow) => {
      const row = sortedRows[rowIndex];
      if (!row) return;
      dispatch(updatePerpetratorInfoRow({ id: row.row_id, changes: { [field]: value } as any }));
    },
    [dispatch, sortedRows],
  );

  const columns: FMRTableColumn<FraudPerpetratorInfoRow>[] = [
    {
      headerName: "Sr. No.",
        field: "sno",
        type: "text" as ColumnType,
        isEditable: false,
        className: "w-[50px]",
      getValue: (row) => row.sno ?? null,
    },

    {
      headerName: "Name of the Perpetrator",
        field: "name_of_perpetrator",
        type: "text" as ColumnType,
        isEditable: true,
        getValue: (row) => row.name_of_perpetrator ?? "",
        onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "name_of_perpetrator"),
      },

      {
        headerName: "Perpetrator Address",
        field: "perpetrator_address",
        type: "text" as ColumnType,
        isEditable: true,
        getValue: (row) => row.perpetrator_address ?? "",
        onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "perpetrator_address"),
      },

      {
        headerName: "Status of Perpetrator/s",
        field: "status_of_perpetrators",
        type: "select" as ColumnType,
        isEditable: true,
        options: PERPETRATOR_STATUS_OPTIONS,
        getValue: (row) => row.status_of_perpetrators ?? null,
        onChange: (rowIndex: number, value: PerpetratorStatus) =>
          handleChange(rowIndex, value, "status_of_perpetrators"),
      },

      {
        headerName: "PAN of Perpetrator",
        field: "pan_of_perpetrator",
        type: "text" as ColumnType,
        isEditable: true,
        getValue: (row) => row.pan_of_perpetrator ?? "",
        onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "pan_of_perpetrator"),
      },

      {
        headerName: "Name of Account where fraud was perpetrated",
        field: "fraud_perpetrated_account_name",
        type: "text" as ColumnType,
        isEditable: true,
        getValue: (row) => row.fraud_perpetrated_account_name ?? "",
      onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "fraud_perpetrated_account_name"),
    },
  ];

  return (
    <FMRTable
      columns={columns}
      rows={sortedRows}
      onAdd={handleAddRow}
      onDelete={handleDeleteRow}
      title={"Perpetrator’s Details"}
    />
  );
};

PerpetratorDetailsTable.displayName = "PerpetratorDetailsTable";
